import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';
import { scheduleService } from '../../../services/scheduleService';

const PaperAssignmentModal = ({ conferenceId, session, tracks, onClose, onAssigned }) => {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [trackFilter, setTrackFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    loadPapers();
  }, [conferenceId]);

  const loadPapers = async () => {
    try {
      setLoading(true);
      const data = await scheduleService?.getUnassignedPapers(conferenceId);
      setPapers(data || []);
    } catch (err) {
      console.error('Load unassigned papers error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = papers?.filter(p => {
    const matchSearch = !search ||
      p?.title?.toLowerCase()?.includes(search?.toLowerCase()) ||
      `${p?.author?.firstName} ${p?.author?.lastName}`?.toLowerCase()?.includes(search?.toLowerCase()) ||
      `#${p?.paperNumber}`?.includes(search);
    const matchTrack = trackFilter === 'all' || p?.trackId === trackFilter;
    return matchSearch && matchTrack;
  });

  const toggleSelect = (paperId) => {
    setSelected(prev =>
      prev?.includes(paperId) ? prev?.filter(id => id !== paperId) : [...prev, paperId]
    );
  };

  const handleAssign = async () => {
    if (!selected?.length) return;
    try {
      setAssigning(true);
      const existingCount = session?.items?.length || 0;
      for (let i = 0; i < selected.length; i++) {
        await scheduleService?.assignPaper(session?.id, selected[i], {
          presentationOrder: existingCount + i,
          durationMinutes: 20,
        });
      }
      onAssigned?.();
      onClose?.();
    } catch (err) {
      console.error('Assign papers error:', err);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground">Assign Papers</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              to: {session?.title}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-md transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-border flex items-center gap-3">
          <div className="flex-1">
            <Input
              placeholder="Search by title, author, or paper #..."
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
            />
          </div>
          <Select
            value={trackFilter}
            onChange={setTrackFilter}
            className="min-w-[11rem]"
            options={[
              { value: 'all', label: 'All tracks' },
              ...(tracks || []).map((tr) => ({ value: tr?.id, label: tr?.name })),
            ]}
          />
        </div>

        {/* Paper List */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader" size={20} className="animate-spin text-primary" />
            </div>
          ) : filtered?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="FileText" size={24} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {papers?.length === 0 ? 'All accepted papers have been assigned' : 'No papers match your search'}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filtered?.map(paper => {
                const isSelected = selected?.includes(paper?.id);
                return (
                  <label
                    key={paper?.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/30'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(paper?.id)}
                      className="w-4 h-4 mt-0.5 text-primary border-border focus:ring-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-primary font-semibold">
                          #{paper?.paperNumber}
                        </span>
                        <span className="text-sm font-medium text-foreground line-clamp-1">
                          {paper?.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>{paper?.author?.firstName} {paper?.author?.lastName}</span>
                        {paper?.author?.institution && (
                          <>
                            <span>&middot;</span>
                            <span className="truncate">{paper?.author?.institution}</span>
                          </>
                        )}
                      </div>
                      {paper?.trackName && (
                        <Badge size="sm" variant="info" className="mt-1.5">{paper?.trackName}</Badge>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {selected?.length > 0 ? `${selected?.length} paper${selected?.length > 1 ? 's' : ''} selected` : 'Select papers to assign'}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              onClick={handleAssign}
              loading={assigning}
              disabled={!selected?.length}
              iconName="Plus"
              iconPosition="left"
            >
              Assign Selected
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaperAssignmentModal;
